var Control                = require("enyo/Control"),
    ScrollButton           = require("enyo-ui/ScrollButton"),
    ScrollMap              = require("enyo-ui/ScrollMap"),
    ScrollThumb            = require("enyo-ui/ScrollThumb"),
    ScrollThumbDragSupport = require("enyo-ui/ScrollThumbDragSupport"),
    ScrollerDecorator      = require("enyo-ui/ScrollerDecorator"),
    ScrollerDecoratorScrollbars,
    kind                   = require("enyo/kind"),
    utils                  = require("enyo/utils");

require("enyo-ui");

/**
 * [ScrollerDecoratorScrollbars]{@link enyo-ui.ScrollerDecoratorScrollbars} is
 * scroller decorator that displays a full scroll bar for each axis as needed.
 *
 * Each scrollbar has two [ScrollButtons]{@link enyo-ui.ScrollButton}, a
 * [ScrollMap]{@link enyo-ui.ScrollMap} as track with a draggable
 * [ScrollThumb]{@link enyo-ui.ScrollThumb} inside it.
 *
 * @ui
 * @class   enyo-ui.ScrollerDecoratorScrollbars
 * @extends enyo-ui.ScrollerDecorator
 * @public
 */
ScrollerDecoratorScrollbars = module.exports = kind(/** @lends  enyo-ui.ScrollerDecoratorScrollbars.prototype */ {

    /**
     * @private
     */
    name: "enyo-ui.ScrollerDecoratorScrollbars",

    /**
     * @private
     */
    kind: ScrollerDecorator,

    /**
     * @private
     */
    classes: "enyo-ui-scroller-decorator-scrollbars",

    /**
     * Set visibility of horizontal scrollbar to "always", "never" or
     * "auto".
     *
     * @type    {string}
     * @default "auto"
     * @public
     */
    horizontal: "auto",

    /**
     * Set visibility of vertical scrollbar to "always", "never" or "auto".
     *
     * @type    {string}
     * @default "auto"
     * @public
     */
    vertical: "auto",

    /**
     * Options for the vertical scrollbar.
     *
     * @type {?object}
     * @default null
     * @public
     */
    scrollbarVerticalOptions: null,

    /**
     * Options for the horizontal scrollbar.
     *
     * @type {?object}
     * @default null
     * @public
     */
    scrollbarHorizontalOptions: null,

    /**
     * The kind to use for scroll buttons.
     *
     * @type    {enyo.Control}
     * @default enyo.Control
     * @public
     */
    scrollbarCornerKind: Control,

    /**
     * Options for the scrollbar corner.
     *
     * @type {?object}
     * @default null
     * @public
     */
    scrollbarCornerOptions: null,

    /**
     * The kind to use for scroll buttons.
     *
     * @type    {enyo.Control}
     * @default enyo-ui.ScrollButton
     * @public
     */
    scrollButtonKind: ScrollButton,

    /**
     * Options for the scroll button used to scroll up.
     *
     * @type {?object}
     * @default null
     * @public
     */
    scrollButtonUpOptions: null,

    /**
     * Options for the scroll button used to scroll down.
     *
     * @type {?object}
     * @default null
     * @public
     */
    scrollButtonDownOptions: null,

    /**
     * Options for the scroll button used to scroll to the left.
     *
     * @type {?object}
     * @default null
     * @public
     */
    scrollButtonLeftOptions: null,

    /**
     * Options for the scroll button used to scroll to the right.
     *
     * @type {?object}
     * @default null
     * @public
     */
    scrollButtonRightOptions: null,

    /**
     * The kind to use as scroll map.
     *
     * @type    {enyo.Control}
     * @default enyo-ui.ScrollMap
     * @public
     */
    scrollMapKind: ScrollMap,

    /**
     * Options for the scroll map.
     *
     * @type {?object}
     * @public
     */
    scrollMapOptions: {
        shouldScrollOnTap: true
    },

    /**
     * The kind to use as scroll thumb.
     *
     * @type    {enyo.Control}
     * @default enyo-ui.ScrollThumb
     * @public
     */
    scrollThumbKind: ScrollThumb,

    /**
     * Options for the scroll thumb.
     *
     * @type {Object=}
     * @public
     */
    scrollThumbOptions: null,

    /**
     * Flag if the decorator should trigger a resize if it applied changes in
     * the chrome configuration.
     *
     * @type {boolean}
     * @public
     */
    resizeOnChromeConfigurationChange: false,

    /**
     * @private
     */
    bindings: [{
        from: ".scroller",
        to:   ".$.scrollButtonUp.scroller"
    }, {
        from: ".scroller",
        to:   ".$.scrollMapVertical.scroller"
    }, {
        from: ".scroller",
        to:   ".$.scrollThumbVertical.scroller"
    }, {
        from: ".$.scrollTrackVertical",
        to:   ".$.scrollThumbVertical.track"
    }, {
        from: ".scroller",
        to:   ".$.scrollButtonDown.scroller"
    }, {
        from: ".scroller",
        to:   ".$.scrollButtonLeft.scroller"
    }, {
        from: ".scroller",
        to:   ".$.scrollMapHorizontal.scroller"
    }, {
        from: ".scroller",
        to:   ".$.scrollThumbHorizontal.scroller"
    }, {
        from: ".$.scrollTrackHorizontal",
        to:   ".$.scrollThumbHorizontal.track"
    }, {
        from: ".scroller",
        to:   ".$.scrollButtonRight.scroller"
    }],

    /**
     * @private
     */
    observers: {
        lazyConfigureChrome: ["horizontal", "vertical"]
    },

    /**
     * Builds the scrollbars.
     *
     * @private
     */
    createDecoration: function() {
        this.createScrollbarCorner();
        this.createVerticalScrollbar();
        this.createHorizontalScrollbar();
    },

    /**
     * Builds the client control.
     *
     * @private
     */
    createScrollbarCorner: function() {
        var scrollbarCornerOptions = this.scrollbarCornerOptions || {};

        this.controlParent = null;
        this.createComponent({
            name:     "scrollbarCorner",
            kind:     this.scrollbarCornerKind,
            classes:  "enyo-ui-scroller-decorator-scrollbars__scrollbar-corner " + (scrollbarCornerOptions.classes || ""),
            isChrome: true,
            showing:  false
        }, scrollbarCornerOptions);
    },

    /**
     * Builds the vertical scrollbar.
     *
     * @private
     */
    createVerticalScrollbar: function() {
        var scrollbar,
            scrollMap,
            scrollTrack,
            scrollbarVerticalOptions = this.scrollbarVerticalOptions || {},
            scrollButtonUpOptions    = this.scrollButtonUpOptions || {},
            scrollMapOptions         = this.scrollMapOptions || {},
            scrollThumbOptions       = this.scrollThumbOptions || {},
            scrollButtonDownOptions  = this.scrollButtonDownOptions || {};

        // Scrollbar
        this.controlParent = null;
        scrollbar          = this.createComponent({
            name:     "scrollbarVertical",
            kind:     Control,
            classes:  "enyo-ui-scroller-decorator-scrollbars__scrollbar-vertical " + (scrollbarVerticalOptions.classes || ""),
            isChrome: true,
            showing:  false
        }, scrollbarVerticalOptions);

        // Scroll up button
        this.controlParent = scrollbar;
        this.createComponent({
            name:      "scrollButtonUp",
            kind:      this.scrollButtonKind,
            classes:   "enyo-ui-scroller-decorator-scrollbars__scroll-button-up " + (scrollButtonUpOptions.classes || ""),
            isChrome:  true,
            direction: "up"
        }, scrollButtonUpOptions);

        // Scroll map
        scrollMap = this.createComponent({
            name:     "scrollMapVertical",
            kind:     this.scrollMapKind,
            classes:  "enyo-ui-scroller-decorator-scrollbars__scroll-map-vertical " + (scrollMapOptions.classes || ""),
            isChrome: true,
            axis:     "vertical"
        }, scrollMapOptions);

        // Scroll track
        this.controlParent = scrollMap;
        scrollTrack        = this.createComponent({
            name:     "scrollTrackVertical",
            kind:     Control,
            classes:  "enyo-ui-scroller-decorator-scrollbars__scroll-track-vertical",
            isChrome: true
        });

        // Scroll thumb
        this.controlParent = scrollTrack;
        this.createComponent({
            name:     "scrollThumbVertical",
            kind:     this.scrollThumbKind,
            mixins:   [ScrollThumbDragSupport],
            classes:  "enyo-ui-scroller-decorator-scrollbars__scroll-thumb-vertical " + (scrollThumbOptions.classes || ""),
            isChrome: true,
            axis:     "vertical"
        }, scrollThumbOptions);

        // Scroll down button
        this.controlParent = scrollbar;
        this.createComponent({
            name:      "scrollButtonDown",
            kind:      this.scrollButtonKind,
            classes:   "enyo-ui-scroller-decorator-scrollbars__scroll-button-down " + (scrollButtonDownOptions.classes || ""),
            isChrome:  true,
            direction: "down"
        }, scrollButtonDownOptions);
    },

    /**
     * Builds the horizontal scrollbar.
     *
     * @private
     */
    createHorizontalScrollbar: function() {
        var scrollbar,
            scrollMap,
            scrollTrack,
            scrollbarHorizontalOptions = this.scrollbarHorizontalOptions || {},
            scrollButtonLeftOptions    = this.scrollButtonLeftOptions || {},
            scrollMapOptions           = this.scrollMapOptions || {},
            scrollThumbOptions         = this.scrollThumbOptions || {},
            scrollButtonRightOptions   = this.scrollButtonRightOptions || {};

        // Scroll bar
        this.controlParent = null;
        scrollbar          = this.createComponent({
            name:     "scrollbarHorizontal",
            kind:     Control,
            classes:  "enyo-ui-scroller-decorator-scrollbars__scrollbar-horizontal " + (scrollbarHorizontalOptions.classes || ""),
            isChrome: true,
            showing:  false
        }, scrollbarHorizontalOptions);

        // Scroll left button
        this.controlParent = scrollbar;
        this.createComponent({
            name:      "scrollButtonLeft",
            kind:      this.scrollButtonKind,
            classes:   "enyo-ui-scroller-decorator-scrollbars__scroll-button-left " + (scrollButtonLeftOptions.classes || ""),
            isChrome:  true,
            direction: "left"
        }, scrollButtonLeftOptions);

        // Scroll map
        scrollMap = this.createComponent({
            name:     "scrollMapHorizontal",
            kind:     this.scrollMapKind,
            classes:  "enyo-ui-scroller-decorator-scrollbars__scroll-map-horizontal " + (scrollMapOptions.classes || ""),
            isChrome: true,
            axis:     "horizontal"
        }, scrollMapOptions);

        // Scroll track
        this.controlParent = scrollMap;
        scrollTrack        = this.createComponent({
            name:     "scrollTrackHorizontal",
            kind:     Control,
            classes:  "enyo-ui-scroller-decorator-scrollbars__scroll-track-horizontal",
            isChrome: true
        });

        // Scroll thumb
        this.controlParent = scrollTrack;
        this.createComponent({
            name:     "scrollThumbHorizontal",
            kind:     this.scrollThumbKind,
            mixins:   [ScrollThumbDragSupport],
            classes:  "enyo-ui-scroller-decorator-scrollbars__scroll-thumb-horizontal " + (scrollThumbOptions.classes || ""),
            isChrome: true,
            axis:     "horizontal"
        }, scrollThumbOptions);

        // Scroll right button
        this.controlParent = scrollbar;
        this.createComponent({
            name:      "scrollButtonRight",
            kind:      this.scrollButtonKind,
            classes:   "enyo-ui-scroller-decorator-scrollbars__scroll-button-right " + (scrollButtonRightOptions.classes || ""),
            isChrome:  true,
            direction: "right"
        }, scrollButtonRightOptions);
    },

    // ...........................
    // Chrome Configuration & Synchronization

    /**
     * Calculate the appropriate visibility for both scrollbars.
     *
     * @return {Object}  The chrome setting
     * @private
     */
    calcChromeConfiguration: function() {
        var horizontal = false,
            vertical   = false,
            scroller   = this.getScroller(),
            horizontalMode,
            verticalMode,
            scrollerDimensions;

        // No scroller
        if (!scroller) {
            return {
                scrollerWidth:           null,
                scrollerHeight:          null,
                contentWidth:            null,
                contentHeight:           null,
                showScrollbarHorizontal: false,
                showScrollbarVertical:   false,
                showScrollbarCorner:     false
            };
        }

        scrollerDimensions = this.get("scrollerDimensions");
        horizontalMode     = scroller.get("horizontal") ? this.get("horizontal") : "never";
        verticalMode       = scroller.get("vertical") ? this.get("vertical") : "never";

        // Horizontal scrollbar visibility
        switch (horizontalMode) {
            case "always":
                horizontal = true;
                break;
            case "never":
                horizontal = false;
                break;
            case "auto":
            default:
                horizontal = scrollerDimensions.contentWidth > scrollerDimensions.scrollerWidth;
                break;
        }

        // Vertical scrollbar visibility
        switch (verticalMode) {
            case "always":
                vertical = true;
                break;
            case "never":
                vertical = false;
                break;
            case "auto":
            default:
                vertical = scrollerDimensions.contentHeight > scrollerDimensions.scrollerHeight;
                break;
        }

        // We include the dimensions of the decorator, scroller and
        // scroller-content to make sure that *if* we run into the same
        // scrollbar visibility twice, this configuration was also reached with
        // the same content dimensions.
        return {
            scrollerWidth:           scrollerDimensions.scrollerWidth,
            scrollerHeight:          scrollerDimensions.scrollerHeight,
            contentWidth:            scrollerDimensions.contentWidth,
            contentHeight:           scrollerDimensions.contentHeight,
            showScrollbarHorizontal: horizontal,
            showScrollbarVertical:   vertical,
            showScrollbarCorner:     horizontal && vertical
        };
    },

    /**
     * Apply the chrome configuration.
     *
     * @param  {Object}   configuration  The chrome configuration to apply
     * @return {Boolean}  Whether the new chrome configuration resized the
     *                    contents of the scroller.
     * @private
     */
    applyChromeConfiguration: function(configuration) {
        var showScrollbarHorizontal    = configuration.showScrollbarHorizontal,
            showScrollbarVertical      = configuration.showScrollbarVertical,
            showScrollbarCorner        = configuration.showScrollbarCorner,
            scrollbarHorizontalShowing = this.$.scrollbarHorizontal.showing,
            scrollbarVerticalShowing   = this.$.scrollbarVertical.showing,
            scrollbarCornerShowing     = this.$.scrollbarCorner.showing,
            client                     = this.$.client,
            changed                    = false;

        if (showScrollbarHorizontal !== scrollbarHorizontalShowing) {
            this[showScrollbarHorizontal ? "showScrollbarHorizontal" : "hideScrollbarHorizontal"]();
            changed = true;
        }

        if (showScrollbarVertical !== scrollbarVerticalShowing) {
            this[showScrollbarVertical ? "showScrollbarVertical" : "hideScrollbarVertical"]();
            changed = true;
        }

        if (showScrollbarCorner !== scrollbarCornerShowing) {
            this[showScrollbarCorner ? "showScrollbarCorner" : "hideScrollbarCorner"]();
            changed = true;
        }

        if (changed) {
            this.resizeOnChromeConfigurationChange && this.resize();

            return client.didResize;
        }
        else {
            return false;
        }
    },

    /**
     * Choose the most appropriate chrome configuration from the supplied list.
     * In this case, more chrome is better as it will make sure the user can
     * navigate to all content inside the scroller.
     *
     * @param  {Array}   configurations  The configurations to choose from
     * @return {Object}  The chosen configuration
     * @private
     */
    solveChromeConflict: function(configurations) {
        var i,
            configuration,
            weight,
            bestConfiguration = {};

        // We search the tried chrome configurations in reverse order, as this
        // ensures we check the most recent representation of the content inside
        // the scroller first. The preferred chrome configuration is the one
        // with the most visible scrollbars as this ensures the user can scroll
        // to all of the scrollers' content. If a choice needs to be made
        // between showing only the horizontal scrollbar or only the vertical
        // scrollbar, the vertical one is always preferred.
        for (i = configurations.length - 1; i >= 0; i--) {
            configuration = configurations[i];
            weight        = 0;

            // Vertical scrollbar is preferred, so we assign it the highest
            // weight.
            if (configuration.showScrollbarVertical) {
                weight += 2;
            }

            if (configuration.showScrollbarHorizontal) {
                weight += 1;
            }

            // A configuration is only better all previous ones if it's weight
            // is higher.
            if (bestConfiguration.index === undefined || weight > bestConfiguration.weight) {
                bestConfiguration.index  = i;
                bestConfiguration.weight = weight;
            }
        }

        // Configuration with highest weight is the best one.
        return configurations[bestConfiguration.index];
    },

    /**
     * Synchronize scroll thumbs.
     *
     * @private
     */
    syncChrome: function() {
        this.$.scrollbarHorizontal.showing && this.$.scrollThumbHorizontal.sync();
        this.$.scrollbarVertical.showing && this.$.scrollThumbVertical.sync();
    },

    // ...........................
    // Scrollbar Visibility

    /**
     * Show the horizontal scrollbar.
     *
     * @private
     */
    showScrollbarHorizontal: function() {
        var scrollbarHorizontal = this.$.scrollbarHorizontal;

        if (scrollbarHorizontal.showing) {
            return;
        }

        this.addClass("enyo-ui-scroller-decorator-scrollbars--has-scrollbar-horizontal");

        // This forces the css display value to update, but doesn't interfere
        // with extensions that might be applied to the `show` and `hide`
        // methods.
        scrollbarHorizontal.show();
    },

    /**
     * Hide the horizontal scrollbar.
     *
     * @private
     */
    hideScrollbarHorizontal: function() {
        var scrollbarHorizontal = this.$.scrollbarHorizontal;

        if (!scrollbarHorizontal.showing) {
            return;
        }

        this.removeClass("enyo-ui-scroller-decorator-scrollbars--has-scrollbar-horizontal");

        // This forces the css display value to update, but doesn't interfere
        // with extensions that might be applied to the `show` and `hide`
        // methods.
        scrollbarHorizontal.hide();
    },

    /**
     * Show the vertical scrollbar.
     *
     * @private
     */
    showScrollbarVertical: function() {
        var scrollbarVertical = this.$.scrollbarVertical;

        if (scrollbarVertical.showing) {
            return;
        }

        this.addClass("enyo-ui-scroller-decorator-scrollbars--has-scrollbar-vertical");

        // This forces the css display value to update, but doesn't interfere
        // with extensions that might be applied to the `show` and `hide`
        // methods.
        scrollbarVertical.show();
    },

    /**
     * Hide the vertical scrollbar.
     *
     * @private
     */
    hideScrollbarVertical: function() {
        var scrollbarVertical = this.$.scrollbarVertical;

        if (!scrollbarVertical.showing) {
            return;
        }

        this.removeClass("enyo-ui-scroller-decorator-scrollbars--has-scrollbar-vertical");

        // This forces the css display value to update, but doesn't interfere
        // with extensions that might be applied to the `show` and `hide`
        // methods.
        scrollbarVertical.hide();
    },

    /**
     * Show the scrollbar corner.
     *
     * @private
     */
    showScrollbarCorner: function() {
        var scrollbarCorner = this.$.scrollbarCorner;

        // This forces the css display value to update, but doesn't interfere
        // with extensions that might be applied to the `show` and `hide`
        // methods.
        !scrollbarCorner.showing && scrollbarCorner.show();
    },

    /**
     * Hide the scrollbar corner.
     *
     * @private
     */
    hideScrollbarCorner: function() {
        var scrollbarCorner = this.$.scrollbarCorner;

        // This forces the css display value to update, but doesn't interfere
        // with extensions that might be applied to the `show` and `hide`
        // methods.
        scrollbarCorner.showing && scrollbarCorner.hide();
    }

});

/**
 * This method allows enyo.kind to extend the `scrollbarCornerOptions`,
 * `scrollButtonUpOptions`, `scrollButtonDownOptions`,
 * `scrollButtonLeftOptions`, `scrollButtonRightOptions`, `scrollMapOptions` and
 * `scrollThumbOptions` hashes when creating super kinds.
 *
 * @param    {Function}  ctor   The constructor
 * @param    {Object}    props  The properties
 * @memberof Repeater
 * @static
 * @private
 */
ScrollerDecoratorScrollbars.concat = function(ctor, props) {
    var proto   = ctor.prototype || ctor,
        options = [
            "scrollbarCornerOptions",
            "scrollButtonUpOptions",
            "scrollButtonDownOptions",
            "scrollButtonLeftOptions",
            "scrollButtonRightOptions",
            "scrollMapOptions",
            "scrollThumbOptions"
        ];

    options.forEach(function(prop) {
        var extension = proto[prop] ? utils.clone(proto[prop]) : {};
        proto[prop]   = utils.mixin(extension, props[prop]);
        delete props[prop];
    });
};
